import { combineEpics } from 'redux-observable';
import { Observable } from 'rxjs';
import { get } from 'lodash';

import * as ActionCreators from '../../actions/ActionCreators';
import * as ActionTypes from '../../actions/ActionTypes';
import { serialize, handleErrors, isHLSSupported } from '../../utils/ASVPUtils';

import {
    UNSUPPORTED_CSS_CLASS,
    ERROR_CSS_CLASS,
    ERROR_FETCHING_SOURCE_SLATE,
    HLS_MIME_TYPE,
    VIDEO_SOURCE_QUERY_PARAMS,
    VIDEO_SOURCE_URL,
    MEDIA_JSON_SOURCE_URL
} from '../../constants';


const receiveVideoSourceEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.RECEIVE_VIDEO_SOURCE)
    .pluck('payload', 'data', 'video')
    .map(data => {
        return {
            collectionTitle: data.collection_title,
            id: data.id,
            poster: data.poster,
            title: data.title,
            titleId: data.title_id,
            type: data.type,
        };
    })
    .map(data => ActionCreators.setVideoSource(data));


const receiveMediaSourceEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.RECEIVE_MEDIA_SOURCE)
    .pluck('payload')
    .map(data => {
        let media = get(data, 'media.desktop.unprotected');
        if (!media) media = get(data, 'media.desktop.bulkaes');

        return {
            duration: media.totalRuntime,
            hasCaptions: !!media.captions && !!media.captions.length,
            contentURL: media.secureUrl,
        };
    })
    .map(data => ActionCreators.setMediaSource(data));


// called when video and media source are finished processing
// because we need both sources for playback
const receiveVideoPlayerDataEpic = (action$, store, { player }) => Observable.merge(
        action$.ofType(ActionTypes.PLAYER_EVENT_READY),
        action$.ofType(ActionTypes.SET_VIDEO_ID)
    )
    .switchMap(() => Observable.combineLatest(
        action$.ofType(ActionTypes.SET_VIDEO_SOURCE),
        action$.ofType(ActionTypes.SET_MEDIA_SOURCE),
    ).take(1))
    .map(() => ActionCreators.receiveVideoPlayerData()); // get video source state from store


// sets the player src and poster from the fetched data sources
const setVideoSourceEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.RECEIVE_VIDEO_PLAYER_DATA)
    .map(() => store.getState().videoSource) // get video source state from store
    .filter(videoSource => !!videoSource.contentURL)
    .do(videoSource => player.poster(videoSource.poster)) // set poster
    .do(videoSource => player.src({ 'type': HLS_MIME_TYPE, 'src': videoSource.contentURL }))
    .do(() => (player.currentType() === HLS_MIME_TYPE && !isHLSSupported()) ? player.addClass(UNSUPPORTED_CSS_CLASS) : player.removeClass(UNSUPPORTED_CSS_CLASS)) // set/unset unsupported css class if can't play media
    .map(() => ActionCreators.setVideoPlayerData());


const resetVideoSourceEpic = (action$, store, { player }) => Observable.merge(
        action$.ofType(ActionTypes.ADS_PREROLL_END),
        action$.ofType(ActionTypes.ADS_MIDROLL_END),
        action$.ofType(ActionTypes.RECONNECT_STREAM),
    )
    .map(() => store.getState().videoSource) // get video source state from store
    .filter(videoSource => !!videoSource.contentURL)
    .do(videoSource => player.src({ 'type': HLS_MIME_TYPE, 'src': videoSource.contentURL }))
    .ignoreElements();


// when player is ready and/or video id is set -- fetch the data based on the videoID in the state
// the initial state might have the video id from how it's passed in on player creation (PLAYER_EVENT_READY)
// SET_VIDEO_ID will have set the video id to a new one.
const fetchVideoSourceEpic = (action$, store, { }) => Observable.merge(
        action$.ofType(ActionTypes.PLAYER_EVENT_READY),
        action$.ofType(ActionTypes.SET_VIDEO_ID)
    )
    .map(() => store.getState().player.videoID)
    .distinctUntilChanged() // stop here if the video id hasn't changed
    .filter(Boolean) // if video id exists
    .switchMap(videoID => Observable.fromPromise(
        fetch(`${VIDEO_SOURCE_URL}${videoID}?${serialize(VIDEO_SOURCE_QUERY_PARAMS)}`) // gets new video data from id
            .then(handleErrors)
            .then(res => res.json())
            .then(response => ActionCreators.receiveVideoSource(response))
            .catch(data => ActionCreators.fetchVideoSourceError(data))
    ));


const fetchMediaSourceEpic = (action$, store, { }) => Observable.merge(
        action$.ofType(ActionTypes.PLAYER_EVENT_READY),
        action$.ofType(ActionTypes.SET_VIDEO_ID)
    )
    .map(() => store.getState().player.videoID)
    .distinctUntilChanged() // stop here if the video id hasn't changed
    .filter(Boolean) // if video id exists
    .switchMap(videoID => Observable.fromPromise(
        fetch(`${MEDIA_JSON_SOURCE_URL}${videoID}/desktop`) // gets new video data from id
            .then(handleErrors)
            .then(res => res.json())
            .then(response => ActionCreators.receiveMediaSource(response))
            .catch(data => ActionCreators.fetchVideoSourceError(data))
    ));


// sets slate for video source error
const fetchVideoSourceError = (action$, store, { player }) => action$
    .ofType(ActionTypes.FETCH_VIDEO_SOURCE_ERROR)
    .do(data => console.error('fetchVideoSourceError', data))
    .do(() => player.addClass(ERROR_CSS_CLASS))
    .do(() => player.pause())
    .do(() => player.poster(ERROR_FETCHING_SOURCE_SLATE))
    .ignoreElements();


export default combineEpics(
    fetchVideoSourceEpic,
    receiveVideoSourceEpic,
    fetchVideoSourceError,
    setVideoSourceEpic,
    fetchMediaSourceEpic,
    receiveMediaSourceEpic,
    receiveVideoPlayerDataEpic,
    resetVideoSourceEpic
);



// WEBPACK FOOTER //
// ./src/player/videoSource/epics.js